import firebase from "firebase/app"
import { MASTER_ROLE } from '@/class/const'
import { RoleId, CauseOfDeath } from '@/enum'
import { RoomMember, RoomMemberData } from './roomMember'

export interface DocUpdateData {
  [fieldKey: string]: string | number | boolean | [];
}

export interface NightActionResult {
  message: string;
  showRoleId?: RoleId;
}

export function getCanNightAction (player: GamePlayerData) {
  if (player.deathDetails) {
    return false
  }

  if (player.status.eatCrab || player.status.tempted) {
    return false
  }

  if (MASTER_ROLE[player.roleId].weapon && player.status.stolen) {
    return false
  }

  return true
}

export interface GamePlayerData extends RoomMemberData {
  id: string;
  name: string;
  roleId: RoleId;
  restShield: number;
  shield: number;
  restBullet: number;
  bullet: number;
  restArmor: number;
  armor: number;
  needSteal: number;
  selectedSelfAppointedRoleId: RoleId | 0;
  selfAppointedRoleId: RoleId | 0;
  isOpened: boolean;
  bomb: boolean;
  selectedPlayerId: string;
  lastNightSelectedPlayerId: string;
  votedPlayerId: string;
  status: Status;
  nightActionResult: NightActionResult[];
  stealCount: number;
  useArmor: boolean;
  detonate: boolean;
  takeOther: boolean;
  isWinner: boolean;
  turnEnd: boolean;
  deathDetails?: DeathDetails;
}

export interface DeathDetails {
  day: number;
  cause: CauseOfDeath;
}

export interface Status {
  eatCrab: boolean;
  tempted: boolean;
  swindled: boolean;
  guard: boolean;
  stolen: boolean;
  success: boolean | null;
  killed: boolean;
}

export class GamePlayer extends RoomMember {
  public data: GamePlayerData;
  private gameId: string;

  constructor (roomId: string, gameId: string, player: GamePlayerData) {
    super(roomId, player)
    this.gameId = gameId
    this.data = player
  }

  private get gameDocRef () {
    return firebase.firestore().doc(`room/${this.roomId}/game/${this.gameId}`)
  }

  public get roleId () {
    return this.data.roleId
  }

  public set roleId (roleId: RoleId) {
    this.data.roleId = roleId
  }

  public get role () {
    return this.roleId ? MASTER_ROLE[this.roleId] : MASTER_ROLE[RoleId.people]
  }

  public get restShield (): number {
    return this.data.restShield
  }

  public set restShield (count) {
    this.data.restShield = count
  }

  public get shield (): number {
    return this.data.shield
  }

  public get restBullet (): number {
    return this.data.restBullet
  }

  public set restBullet (count) {
    this.data.restBullet = count
  }

  public get bullet (): number {
    return this.data.bullet
  }

  public get restArmor (): number {
    return this.data.restArmor
  }

  public set restArmor (count) {
    this.data.restArmor = count
  }

  public get armor (): number {
    return this.data.armor
  }

  public get needSteal (): number {
    return this.data.needSteal
  }

  public set needSteal (count) {
    this.data.needSteal = count
  }

  public get votedPlayerId (): string {
    return this.data.votedPlayerId
  }

  public set votedPlayerId (votedPlayerId: string) {
    this.data.votedPlayerId = votedPlayerId
  }

  public updateVotedPlayerId () {
    this.updateKeys({'votedPlayerId': this.votedPlayerId})
  }

  public get selectedPlayerId (): string {
    return this.data.selectedPlayerId
  }

  public set selectedPlayerId (selectedPlayerId: string) {
    this.data.selectedPlayerId = selectedPlayerId
  }

  public get canNightSelect() {
    if (this.isDead) {
      return false
    }

    if (this.roleId === RoleId.sniper && this.bullet === 0) {
      return false
    }

    if (this.roleId === RoleId.survivor) {
      return false
    }

    if (this.role.activeIn.night) {
      return true
    }

    return false
  }

  public updateSelectedPlayerId () {
    this.updateKeys({
      'selectedPlayerId': this.selectedPlayerId,
      'detonate': false
    })
  }

  public get isDead (): boolean {
    return !!this.data.deathDetails
  }

  public get isOpened (): boolean {
    return this.data.isOpened
  }

  public get status (): Status {
    return this.data.status
  }

  public get useArmor () {
    return this.data.useArmor
  }

  public set useArmor (val: boolean) {
    this.data.useArmor = val
  }

  public toggleUseArmor () {
    this.useArmor = !this.useArmor
    this.updateKeys({'useArmor': this.useArmor})
  }

  public get detonate () {
    return this.data.detonate
  }

  public set detonate (val: boolean) {
    this.data.detonate = val
  }

  public toggleDetonate () {
    this.detonate = !this.detonate
    this.updateKeys({
      'selectedPlayerId': '',
      'detonate': this.detonate
    })
  }

  public get takeOther () {
    return this.data.takeOther
  }

  public set takeOther (val: boolean) {
    this.data.takeOther = val
  }

  public get stealCount (): number {
    return this.data.stealCount
  }

  public get bomb () {
    return this.data.bomb
  }

  public set bomb (flg: boolean) {
    this.data.bomb = flg
  }

  public get isWinner () {
    return this.data.isWinner
  }

  public set isWinner (result: boolean) {
    this.data.isWinner = result
  }

  public get turnEnd () {
    return this.data.turnEnd
  }

  public set turnEnd (val) {
    this.data.turnEnd = val
  }

  public updateTurnEnd (turnEnd: boolean) {
    this.turnEnd = turnEnd
    this.updateKeys({'turnEnd': turnEnd})
  }

  public get lastNightSelectedPlayerId () {
    return this.data.lastNightSelectedPlayerId
  }

  public set lastNightSelectedPlayerId (playerId: string) {
    this.data.lastNightSelectedPlayerId = playerId
  }

  public get nightActionResult (): NightActionResult[] {
    return this.data.nightActionResult
  }

  public get selfAppointedRoleId () {
    return this.data.selfAppointedRoleId
  }

  public get selectedSelfAppointedRoleId () {
    return this.data.selectedSelfAppointedRoleId
  }

  public set selectedSelfAppointedRoleId (roleId: 0 | RoleId) {
    this.data.selectedSelfAppointedRoleId = roleId
  }

  public updateSelectedSelfAppointedRoleId () {
    this.updateKeys({'selectedSelfAppointedRoleId': Number(this.selectedSelfAppointedRoleId)})
  }

  public comingOut () {
    this.data.selfAppointedRoleId = this.selectedSelfAppointedRoleId
    this.updateKeys({'selfAppointedRoleId': this.selfAppointedRoleId})
  }

  public setNightActionResult (message: string, showRoleId?: RoleId): void {
    const data: NightActionResult = { message }
    if (showRoleId) {
      data.showRoleId = showRoleId
    }
    this.data.nightActionResult.push(data)
  }

  public setStatus (status: Status): void {
    this.data.status = status
  }

  public eatCrab (): void {
    this.data.status.eatCrab = true
  }

  public tempted (): void {
    this.data.status.tempted = true
  }

  public swindled (): void {
    this.data.status.swindled = true
  }

  public guard (): void {
    this.data.status.guard = true
  }

  public stolen (): void {
    this.data.status.stolen = true
  }

  public stole (): void {
    this.data.status.success = true
    this.data.stealCount++
  }

  public resetStatus (): void {
    this.data.status = {
      eatCrab: false,
      tempted: false,
      swindled: false,
      guard: false,
      stolen: false,
      success: null,
      killed: false
    }

    this.lastNightSelectedPlayerId = ''
  }

  public get deathDetails () {
    return this.data.deathDetails
  }

  public get canNightAction () {
    return getCanNightAction(this.data)
  }

  public death (day: number, cause: CauseOfDeath): void {
    this.data.deathDetails = { day, cause }
    this.status.killed = true
  }

  public open (): void {
    this.data.isOpened = true
  }

  public resetVotedPlayerId (): void {
    this.data.votedPlayerId = ''
  }

  public resetSelectedPlayerId (): void {
    this.data.selectedPlayerId = ''
  }

  public resetTurnEnd (): void {
    this.data.turnEnd = false
  }

  public get nameAndRole (): string {
    return `${this.name}(${this.role.name})`
  }

  public updateKeys (newData: DocUpdateData) {
    const updateData: DocUpdateData = {}
    Object.entries(newData).forEach(([key, value]) => updateData[`players.${this.id}.${key}`] = value)
    this.gameDocRef.update(updateData)
  }
}

